"use client";

import React, { useMemo } from "react";

import { buildStudentListReportTitle } from "@/app/(internal)/panel/reports/_helpers/build-student-list-report-title.helper";
import { Button } from "@/components/flowbite/button";
import { Dropdown, DropdownButton, DropdownItem, DropdownItems } from "@/components/flowbite/dropdown";
import { ListOption } from "@/components/flowbite/list-option/list-option";
import Typography from "@/components/flowbite/typography/typography";
import { TablerIconTableShareIcon } from "@/components/icons/tabler-icon-table-share.icon";
import { DownloadIcon } from "@/components/icons-lib";
import { useExportStudentListReportMutation } from "@/providers/store/api/reports/reports";
import {
  type IStudentListPreviewRequest,
  ReportExportFormat,
} from "@/providers/store/api/reports/reports.interface";
import { useToast } from "@/providers/toasts/toasts.provider";
import { useWorkspaceTabs } from "@/providers/workspace-tabs/workspace-tabs.provider";
import { extractErrorDescription } from "@/shared/helpers/extract-error-description.helper";
import type { IApiResponseError } from "@/shared/interfaces/api-response.interface";

import type { ShapeItem } from "./shape-item.types";

interface StudentListReportActionsProps {
  selectionShapes: ShapeItem[];
  getPreviewRequest: () => IStudentListPreviewRequest | null;
}

export const StudentListReportActions: React.FC<StudentListReportActionsProps> = ({
  selectionShapes,
  getPreviewRequest,
}) => {
  const { openReport } = useWorkspaceTabs();
  const { showToast } = useToast();
  const [exportStudentListReport, { isLoading: isExporting }] = useExportStudentListReportMutation();

  const reportTitle = useMemo(
    () => buildStudentListReportTitle(selectionShapes),
    [selectionShapes],
  );

  const handleOpenPreview = () => {
    const request = getPreviewRequest();
    if (!request) {
      return;
    }

    const tabId = `student-list-preview:${Date.now()}`;
    sessionStorage.setItem(tabId, JSON.stringify({ request, title: reportTitle }));
    openReport(tabId, reportTitle, true, "custom");
  };

  const handleDownload = async (format: ReportExportFormat) => {
    const request = getPreviewRequest();
    if (!request) {
      return;
    }

    const result = await exportStudentListReport({
      ...request,
      format,
      title: reportTitle,
    });
    const apiError =
      result.error && "data" in result.error ? (result.error.data as IApiResponseError) : null;
    if (apiError) {
      showToast({
        type: "error",
        message: "Failed to download student list",
        description: extractErrorDescription(apiError),
      });
    }
  };

  const canOpenPreview = Boolean(getPreviewRequest());
  const actionIconClassName = "h-6 w-6 shrink-0 text-[#161515]";
  const actionButtonClassName = "min-w-0 flex-1 cursor-pointer px-3 text-[#161515]";

  return (
    <div className="flex w-full flex-row gap-2">
      <Button
        className={actionButtonClassName}
        disabled={!canOpenPreview}
        size="small"
        variant="secondary"
        onClick={handleOpenPreview}
      >
        <TablerIconTableShareIcon className={actionIconClassName} />
        <Typography.SubTitle>Open Preview</Typography.SubTitle>
      </Button>
      {canOpenPreview ? (
        <Dropdown className="min-w-0 flex-1">
          {({ open }) => (
            <>
              <DropdownButton className="w-full">
                <Button
                  className={`w-full ${actionButtonClassName}`}
                  data-active={open}
                  disabled={isExporting}
                  size="small"
                  variant="secondary"
                >
                  <DownloadIcon className={actionIconClassName} />
                  <Typography.SubTitle>Download</Typography.SubTitle>
                </Button>
              </DropdownButton>
              <DropdownItems
                anchor="top end"
                className="z-[60] !mb-2 !mt-0 p-1"
              >
                <DropdownItem variant="unstyled">
                  <ListOption
                    icon={DownloadIcon}
                    label="Export as Excel"
                    size="small"
                    onClick={() => {
                      handleDownload(ReportExportFormat.EXCEL).catch(() => undefined);
                    }}
                  />
                </DropdownItem>
                <DropdownItem variant="unstyled">
                  <ListOption
                    icon={DownloadIcon}
                    label="Export as CSV"
                    size="small"
                    onClick={() => {
                      handleDownload(ReportExportFormat.CSV).catch(() => undefined);
                    }}
                  />
                </DropdownItem>
              </DropdownItems>
            </>
          )}
        </Dropdown>
      ) : (
        <Button
          disabled
          className={actionButtonClassName}
          size="small"
          variant="secondary"
        >
          <DownloadIcon className="h-6 w-6 shrink-0" />
          <Typography.SubTitle>Download</Typography.SubTitle>
        </Button>
      )}
    </div>
  );
};
